
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { FileType2, FileDown, Copy, Check, AlertTriangle, Download } from 'lucide-react';
import { DeepAnalysisResult } from '@/types/analysisTypes';
import { Badge } from '@/components/ui/badge';
import { PriorityIcon } from './PriorityIcon';
import { exportPageRecommendationsToPDF } from './pdfExport';
import { toast } from 'sonner';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface RecommendationsDisplayProps {
  pageAnalysisResult: DeepAnalysisResult;
  urlInput: string;
  handleExportRecommendations: () => void;
}

export const RecommendationsDisplay: React.FC<RecommendationsDisplayProps> = ({
  pageAnalysisResult,
  urlInput,
  handleExportRecommendations
}) => {
  const [showAllTips, setShowAllTips] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  
  // Get actionable fixes from result
  const actionableFixes = pageAnalysisResult.actionable_fixes || [];
  
  // Get top 10 recommendations or all if showAllTips is true
  const displayFixes = showAllTips 
    ? actionableFixes 
    : actionableFixes.slice(0, 10);

  // Find weakest category
  const weakestCategory = Object.entries(pageAnalysisResult.category_scores || {})
    .filter(([_, score]) => typeof score === 'number')
    .sort(([_, scoreA], [__, scoreB]) => (scoreA as number) - (scoreB as number))
    .shift();

  // Handle PDF export of recommendations
  const handleExportPDF = () => {
    exportPageRecommendationsToPDF(pageAnalysisResult, urlInput);
    toast.success('PDF export started');
  };

  // Copy single recommendation to clipboard
  const copyRecommendation = (recommendation: string, index: string) => {
    navigator.clipboard.writeText(recommendation);
    toast.success('Copied to clipboard');
    
    // Track which item was copied for visual feedback
    setExpandedItems(prev => ({...prev, [index]: true}));
    setTimeout(() => {
      setExpandedItems(prev => ({...prev, [index]: false}));
    }, 1500);
  };

  // Copy all recommendations to clipboard
  const copyAllRecommendations = () => {
    const allRecs = actionableFixes.map(fix => 
      `Current Issue: ${fix.current_issue}\nOld Score: ${fix.old_score}\nSuggested Fix: ${fix.suggested_fix}\nRationale: ${fix.rationale}\nNew Score: ${fix.new_score}`
    ).join('\n\n');
    
    navigator.clipboard.writeText(allRecs);
    setCopied(true);
    toast.success('All recommendations copied to clipboard');
    
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };

  // Toggle show all recommendations
  const toggleShowAll = () => {
    setShowAllTips(!showAllTips);
  };

  // Export JSON to clipboard
  const exportJson = () => {
    const jsonData = JSON.stringify(pageAnalysisResult.actionable_fixes, null, 2);
    navigator.clipboard.writeText(jsonData);
    toast.success('JSON copied to clipboard');
  };

  // Toggle row expansion
  const toggleRowExpansion = (index: number) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Helper to truncate text and show expand option
  const renderExpandableText = (text: string, index: number, maxLength: number = 100) => {
    const isExpanded = expandedRows[index];
    const shouldTruncate = text.length > maxLength;
    
    if (!shouldTruncate) {
      return <span>{text}</span>;
    }
    
    return (
      <div className="space-y-2">
        <span>
          {isExpanded ? text : `${text.slice(0, maxLength)}...`}
        </span>
        <button
          onClick={() => toggleRowExpansion(index)}
          className="block text-xs text-primary hover:text-primary/80 font-medium transition-colors"
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      </div>
    );
  };

  // Format category name for display
  const formatCategoryName = (key: string): string => {
    if (key === 'EEAT') return 'E-E-A-T';
    return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  };

  if (actionableFixes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center animate-fade-in">
        <AlertTriangle className="h-12 w-12 text-yellow-500 mb-4 animate-pulse" />
        <h3 className="text-lg font-medium mb-2">No Actionable Fixes Available</h3>
        <p className="text-muted-foreground">
          No actionable fixes could be generated for this page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div className="flex items-center gap-2">
          <h4 className="text-md font-medium bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">Actionable Fixes</h4>
          {weakestCategory && (
            <Badge variant="outline" className="capitalize animate-fade-in bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20">
              Weakest: {formatCategoryName(weakestCategory[0])}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={copyAllRecommendations}
            className={`transition-all border-amber-500/20 ${copied ? 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400' : 'hover:bg-amber-500/10 hover:text-amber-700'}`}
          >
            {copied ? (
              <Check className="w-4 h-4 mr-1 text-green-500 animate-scale" />
            ) : (
              <Copy className="w-4 h-4 mr-1" />
            )}
            Copy All
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleExportRecommendations}
            className="border-amber-500/20 hover:bg-amber-500/10 hover:text-amber-700"
          >
            <FileType2 className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleExportPDF}
            className="border-amber-500/20 hover:bg-amber-500/10 hover:text-amber-700"
          >
            <FileDown className="w-4 h-4 mr-1" />
            Export PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportJson}
            className="border-amber-500/20 hover:bg-amber-500/10 hover:text-amber-700"
          >
            <Download className="w-4 h-4 mr-1" />
            Copy JSON
          </Button>
        </div>
      </div>
      
      {/* Display as table */}
      <div className="rounded-md border border-amber-500/20 overflow-hidden bg-gradient-to-br from-amber-500/5 to-orange-500/5">
        <Table>
          <TableHeader className="bg-amber-500/10">
            <TableRow>
              <TableHead className="text-amber-700 dark:text-amber-400">Current Issue</TableHead>
              <TableHead className="text-amber-700 dark:text-amber-400">Old Score</TableHead>
              <TableHead className="text-amber-700 dark:text-amber-400">Suggested Fix</TableHead>
              <TableHead className="text-amber-700 dark:text-amber-400">Rationale</TableHead>
              <TableHead className="text-amber-700 dark:text-amber-400">New Score</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayFixes.map((fix, idx) => (
              <TableRow key={idx} className="hover:bg-amber-500/5 transition-colors">
                <TableCell className="font-medium min-w-[200px]">{fix.current_issue || 'N/A'}</TableCell>
                <TableCell className="min-w-[100px]">{fix.old_score || 'N/A'}</TableCell>
                <TableCell className="min-w-[300px] max-w-[400px]">
                  {renderExpandableText(fix.suggested_fix, idx * 2, 150)}
                </TableCell>
                <TableCell className="min-w-[250px] max-w-[350px]">
                  {renderExpandableText(fix.rationale, idx * 2 + 1, 120)}
                </TableCell>
                <TableCell className="font-medium text-green-600 min-w-[100px]">{fix.new_score || 'N/A'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      
      {actionableFixes.length > 10 && (
        <div className="flex justify-center mt-4">
          <Button
            variant="outline"
            onClick={toggleShowAll}
            className="transition-all hover:bg-amber-500/10 border-amber-500/20 text-amber-700 dark:text-amber-400"
          >
            {showAllTips ? 'Show Less' : `Show All (${actionableFixes.length})`}
          </Button>
        </div>
      )}
    </div>
  );
};
